import type { EntitlementDecision } from '@tabbio/types';

/**
 * Dev-only, global paywall bypass.
 *
 * When enabled, every client-side entitlement decision resolves to `allowed`,
 * so all `EntitlementGate` paywalls open and AI action guards pass. It is the
 * single switch behind both entitlement chokepoints (`useEntitlement` and the
 * direct `evaluateEntitlement` call in `useCvSectionPreview`).
 *
 * Hard-gated by `__DEV__` so it can NEVER take effect in a production build, and
 * opt-in via env so it stays off by default. Enable it by setting
 * `EXPO_PUBLIC_BYPASS_PAYWALL=true` in `apps/mobile/.env`, then restart the
 * bundler (`EXPO_PUBLIC_*` values are inlined at build time, not read at runtime).
 *
 * NOTE: This only unlocks the client. Server-metered AI actions still enforce
 * the real plan: pair it with `TABBIO_DEV_SUBSCRIPTION_TIER=pro` in
 * `apps/api/.env` for an end-to-end unlocked dev experience.
 */
export function isPaywallBypassEnabled(): boolean {
  return __DEV__ && process.env.EXPO_PUBLIC_BYPASS_PAYWALL === 'true';
}

/**
 * Returns the decision unchanged unless the dev bypass is enabled, in which case
 * it is forced to an `allowed` verdict while preserving its `featureKey` /
 * `actionKey` / `modelPolicy` shape so downstream consumers keep working.
 */
export function applyPaywallBypass(
  decision: EntitlementDecision,
): EntitlementDecision {
  if (!isPaywallBypassEnabled()) {
    return decision;
  }

  return {
    ...decision,
    allowed: true,
    reasonCode: 'allowed',
    promptKey: null,
    recommendedPlanSet: null,
    recommendedTier: null,
    remainingActionsToday: null,
    remainingCreditsToday: null,
    remainingCreditsThisCycle: null,
    estimatedCredits: null,
    limitBehavior: 'none',
  };
}
